import { useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import type * as THREE from "three";

export interface Zone {
  name: string;
  center: [number, number, number];
  /** Half-extent along world X (so total width = halfWidth * 2) */
  halfWidth: number;
  /** Half-extent along world Z (so total depth = halfDepth * 2) */
  halfDepth: number;
}

// Scene is split by the X=0 and Z=0 axes into 4 disjoint quadrants, each
// half = 25 (50x50 each, full scene 100x100). A small center hub at origin
// overlaps the 4 quadrants but wins via the closest-center tiebreaker.
const QUADRANT_HALF = 25;
const QUADRANT_OFFSET = QUADRANT_HALF; // centers sit on the diagonals at (±25, ±25)

export const ZONES: Zone[] = [
  // Center hub — listed first so any tie defers to the more specific zone name
  { name: "center", center: [0, 0, 0], halfWidth: 5, halfDepth: 5 },
  // arch: −X / +Z quadrant (airports, towers, urban developments)
  { name: "arch", center: [-QUADRANT_OFFSET, 0, QUADRANT_OFFSET], halfWidth: QUADRANT_HALF, halfDepth: QUADRANT_HALF },
  // projects: +X / +Z quadrant (software / web dev / AI tools)
  { name: "projects", center: [QUADRANT_OFFSET, 0, QUADRANT_OFFSET], halfWidth: QUADRANT_HALF, halfDepth: QUADRANT_HALF },
  // varch: +X / −Z quadrant (metaverse, Decentraland)
  { name: "varch", center: [QUADRANT_OFFSET, 0, -QUADRANT_OFFSET], halfWidth: QUADRANT_HALF, halfDepth: QUADRANT_HALF },
  // product: −X / −Z quadrant (Infini Loops)
  { name: "product", center: [-QUADRANT_OFFSET, 0, -QUADRANT_OFFSET], halfWidth: QUADRANT_HALF, halfDepth: QUADRANT_HALF },
];

const CHECK_INTERVAL = 10; // check every N frames
const DEBOUNCE_MS = 500; // ms before confirming zone change

function distanceXZ(
  ax: number,
  az: number,
  bx: number,
  bz: number
): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

function isInsideZone(px: number, pz: number, zone: Zone): boolean {
  const dx = Math.abs(px - zone.center[0]);
  const dz = Math.abs(pz - zone.center[2]);
  return dx <= zone.halfWidth && dz <= zone.halfDepth;
}

export default function useZoneDetection(
  avatarPosition: React.RefObject<THREE.Vector3> | { x: number; z: number }
) {
  const [currentZone, setCurrentZone] = useState<string | null>(null);
  const frameCount = useRef(0);
  const pendingZone = useRef<string | null>(null);
  const pendingTimestamp = useRef<number>(0);

  useFrame(() => {
    frameCount.current += 1;
    if (frameCount.current % CHECK_INTERVAL !== 0) return;

    // Read position from ref or plain object
    let px: number, pz: number;
    if ("current" in avatarPosition) {
      const vec = avatarPosition.current;
      if (!vec) return;
      px = vec.x;
      pz = vec.z;
    } else {
      px = avatarPosition.x;
      pz = avatarPosition.z;
    }

    // Find the containing zone whose center is closest to the avatar
    // (handles overlapping rectangles deterministically).
    let detectedZone: string | null = null;
    let closestDist = Infinity;

    for (const zone of ZONES) {
      if (!isInsideZone(px, pz, zone)) continue;
      const dist = distanceXZ(px, pz, zone.center[0], zone.center[2]);
      if (dist < closestDist) {
        closestDist = dist;
        detectedZone = zone.name;
      }
    }

    const now = performance.now();

    // If detected zone matches current, clear any pending change
    if (detectedZone === currentZone) {
      pendingZone.current = null;
      pendingTimestamp.current = 0;
      return;
    }

    // If detected zone matches pending, check debounce timer
    if (detectedZone === pendingZone.current) {
      if (now - pendingTimestamp.current >= DEBOUNCE_MS) {
        setCurrentZone(detectedZone);
        pendingZone.current = null;
        pendingTimestamp.current = 0;
      }
      return;
    }

    // New pending zone
    pendingZone.current = detectedZone;
    pendingTimestamp.current = now;
  });

  return currentZone;
}
